import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser, login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("No sign-in session was returned. Please try signing in again.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        setUser(data);
        window.history.replaceState(null, "", location.pathname);
        navigate("/editor", { replace: true, state: { user: data } });
      } catch (e) {
        setError(errorMessage(e, "We couldn't complete sign-in. Please try again."));
      }
    })();
  }, [location, navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="auth-callback-error">
        <div className="card-surface p-8 max-w-md text-center space-y-4">
          <span className="mx-auto h-12 w-12 rounded-full bg-destructive/15 text-destructive grid place-items-center"><AlertCircle size={22} /></span>
          <h1 className="text-xl font-semibold">Sign-in didn't go through</h1>
          <p className="text-sm text-muted-foreground" data-testid="auth-error-detail">{error}</p>
          <div className="flex gap-2 justify-center pt-2">
            <Button onClick={login} className="btn-pill" data-testid="auth-retry-button">Try again</Button>
            <Button variant="ghost" onClick={() => navigate("/", { replace: true })} className="btn-pill" data-testid="auth-home-button">Back home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="h-3 w-3 rounded-full bg-primary pulse-dot" />
    </div>
  );
}
